<template>
  <Header :title="t('common.select_language')" />

  <div class="change-language-container">
    <div class="change-language-card">
      <div>
        <h1 class="title">{{ t('common.select_language') }}</h1>
        <p class="subtitle">{{ t('common.language_subtitle') }}</p>
        <div class="language-list">
          <label v-for="lang in languages" :key="lang.value" class="language-option">
            <input type="radio" v-model="selectedLanguage" :value="lang.value" />
            <img :src="lang.flag" :alt="lang.label + ' flag'" class="flag" />
            <span>{{ lang.label }}</span>
          </label>
        </div>
      </div>

      <div class="button-row">
        <button class="cancel-btn" @click="onCancel">{{ t('common.cancel') }}</button>
        <button class="save-btn" @click="onSave">{{ t('common.save') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Header from '@/components/Header.vue'
import { put, get } from '@/utils/api'
import { showToast } from '@/utils/toast'

const router = useRouter()
const { t, locale } = useI18n()
const selectedLanguage = ref(locale.value)

const languages = [
  { value: 'en', label: 'English', flag: 'https://flagcdn.com/gb.svg' },
  { value: 'fr', label: 'French', flag: 'https://flagcdn.com/fr.svg' },
  { value: 'de', label: 'German', flag: 'https://flagcdn.com/de.svg' },
  { value: 'es', label: 'Spanish', flag: 'https://flagcdn.com/es.svg' },
]

// Fetch user data on mount
onMounted(async () => {
  try {
    const userId = JSON.parse(localStorage.getItem('UserData'))?.id
    if (userId) {
      const response = await get(`/v1/users/${userId}`)
      if (response?.data?.data?.selected_language_type) {
        selectedLanguage.value = response.data.data.selected_language_type
      }
    }
  } catch (error) {}
})

function onCancel() {
  router.back()
}

async function onSave() {
  const userId = JSON.parse(localStorage.getItem('UserData'))?.id
  const userData = JSON.parse(localStorage.getItem('UserData'))
  const language = selectedLanguage.value

  try {
    locale.value = language

    const res = await put(`/v1/users/${userId}`, {
      ...userData,
      selected_language_type: language,
    })
    if (res) {
      localStorage.setItem('language', language)
      localStorage.setItem('UserData', JSON.stringify(res.data?.data))

      showToast({
        title: t('common.success'),
        message: t('common.language_saved_successfully'),
        type: 'success',
      })
    }

    router.back()
  } catch (error) {
    showToast({
      title: t('common.error'),
      message: t('common.failed_to_save_language'),
      type: 'error',
    })
  }
}
</script>

<style scoped>
.change-language-container {
  background: var(--secondary-color);
  display: flex;
  align-items: flex-start;
  justify-content: space-evenly;
  height: 100%;
}

.change-language-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(16, 30, 54, 0.08);
  padding: 48px 40px 40px 40px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  height: 90%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.title {
  color: var(--primary-color);
  font-family: 'Archivo', sans-serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  margin-bottom: 8px;
}

.subtitle {
  color: #667085;
  font-family: 'Archivo', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 25px;
  margin-bottom: 32px;
  text-overflow: ellipsis;
  overflow: hidden;
}

.language-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 40px;
}

.language-option {
  display: flex;
  align-items: center;
  gap: 16px;
  font-family: 'Archivo', sans-serif;
  font-size: 18px;
  font-weight: 400;
  cursor: pointer;
}

.language-option input[type='radio'] {
  accent-color: var(--primary-color);
  width: 20px;
  height: 20px;
}

.flag {
  width: 32px;
  height: 22px;
  border-radius: 3px;
  object-fit: cover;
  border: 1px solid #e0e0e0;
}

.button-row {
  display: flex;
  gap: 16px;
  justify-content: flex-start;
}

.cancel-btn {
  background: #fff;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  border-radius: 6px;
  padding: 8px 32px;
  font-family: 'Archivo', sans-serif;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.cancel-btn:hover {
  background: #f0f8ff;
}

.save-btn {
  background: var(--primary-color);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 32px;
  font-family: 'Archivo', sans-serif;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.save-btn:hover {
  background: #0d6efd;
}
</style>
